import { useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import { useIsHydrated } from "shared/lib/react/useIsHydrated"

import { ProductCategoryObj } from "scrivito/Objs/ProductCategory/ProductCategoryObjClass"
import { useDebounce } from "shared/lib/performance/useDebounce"
import { useUrlParamStore } from "shared/lib/url/useUrlParams"

import { useProductGroupOptions } from "entities/product-groups/model/useProductGroupOptions"
import { useProductFilter } from "entities/products/model/useProductFilter"
import { productFilterFormDataSchema } from "entities/products/types/ProductFilter"
import { useProductCategoryFilterStore } from "widgets/product-filter/model/useProductCategoryFilterStore"

export function useProductCategoryPageModel({
  page: category
}: {
  page: ProductCategoryObj
}) {
  const { filterState, setFilters } = useProductCategoryFilterStore({
    productCategoryID: category.id()
  })
  const [searchParams] = useSearchParams()
  const isHydrated = useIsHydrated()

  const { values, setValues } = useUrlParamStore(productFilterFormDataSchema, {
    state: filterState,
    setState: setFilters
  })

  const productGroupOptions = useProductGroupOptions({
    productCategoryObj: category
  })

  // Seeding the debounce with the term from the URL lets a page that is opened with
  // a search show its results right away. The delay only exists to keep typing from
  // re-filtering the whole category on every keystroke.
  const debouncedSearchTerm = useDebounce(
    values.searchTerm,
    400,
    searchParams.get("searchTerm")
  )

  const debouncedFilterState = useMemo(
    () => ({
      ...values,
      // The seeded term is browser-only state and must not reach the first render.
      searchTerm: isHydrated ? debouncedSearchTerm : null
    }),
    [values, debouncedSearchTerm, isHydrated]
  )

  const filteredProducts = useProductFilter(debouncedFilterState, {
    category
  })

  return {
    category,
    defaultValues: values,
    productGroupOptions,
    filter: debouncedFilterState,
    products: filteredProducts,
    onChange: setValues
  }
}
